/**
 * First-touch UTM attribution capture — RGPD consent-aware.
 *
 * captureAttribution(): call once at app boot — parses ?utm_* params + referrer
 * into MEMORY only. Nothing is persisted or sent until the visitor grants the
 * matching consent category:
 *   - analytics  → /api/track/visit ping + fh_visitor_id
 *   - marketing  → fh_attr first-touch persistence + /api/track/attribution
 *
 * flushAttribution(): call right after login — attaches the stored first-touch
 * data to the authenticated user (backend is write-once and also enforces the
 * consent header server-side).
 */
import axios from 'axios';
import { consentHeaders, hasConsent } from './consent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ATTR_KEY = 'fh_attr';
const VISITOR_KEY = 'fh_visitor_id';
const SESSION_SENT_KEY = 'fh_visit_sent';

let pending = null;
let listening = false;

const randomId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

function persistAttr(data) {
  if (!localStorage.getItem(ATTR_KEY) && (data.source || data.referrer)) {
    localStorage.setItem(ATTR_KEY, JSON.stringify({
      source: data.source,
      medium: data.medium,
      campaign: data.campaign,
      content: data.content,
      term: data.term,
      referrer: data.referrer,
      first_seen_at: data.first_seen_at,
    }));
  }
}

function sendVisit(data) {
  if (sessionStorage.getItem(SESSION_SENT_KEY)) return;
  sessionStorage.setItem(SESSION_SENT_KEY, '1');
  let visitorId = localStorage.getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId = randomId();
    localStorage.setItem(VISITOR_KEY, visitorId);
  }
  axios.post(`${BACKEND_URL}/api/track/visit`, {
    visitor_id: visitorId,
    source: data.source,
    medium: data.medium,
    campaign: data.campaign,
    content: data.content,
    term: data.term,
    referrer: data.referrer,
    path: data.path,
    lang: data.lang,
  }, { headers: consentHeaders() }).catch(() => {});
}

function flushPending() {
  try {
    if (!pending) return;
    if (hasConsent('marketing')) persistAttr(pending);
    if (hasConsent('analytics')) sendVisit(pending);
  } catch { /* analytics must never break the app */ }
}

export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    pending = {
      source: params.get('utm_source') || params.get('ref') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || '',
      referrer: document.referrer || '',
      path: window.location.pathname,
      lang: navigator.language || '',
      first_seen_at: new Date().toISOString(),
    };
    flushPending();
    if (!listening) {
      listening = true;
      window.addEventListener('fh:consent-changed', flushPending);
    }
  } catch {
    /* analytics must never break the app */
  }
}

export async function flushAttribution() {
  try {
    if (!hasConsent('marketing')) return;
    const raw = localStorage.getItem(ATTR_KEY);
    if (!raw) return;
    await axios.post(`${BACKEND_URL}/api/track/attribution`, JSON.parse(raw), { headers: consentHeaders() });
    localStorage.removeItem(ATTR_KEY);
  } catch {
    /* keep stored attr for a later retry */
  }
}
